import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { HttpStatusCode } from "axios";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import {
  messagingApi,
  useGetProposalCommentConversationsQuery,
  type ProposalCommentConversationJSON,
} from "messaging/_api/messaging-api";
import {
  CorrespondenceBody,
  CorrespondenceComposerSlot,
  CorrespondenceFrame,
  CorrespondenceJumpToBottom,
  CorrespondenceHeader,
  CorrespondenceSearchBar,
  correspondenceListPadding,
} from "messaging/correspondence/_views/correspondence-layout";
import CorrespondenceTabs from "messaging/correspondence/_views/correspondence-tabs";
import { getForumUnreadCounts } from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import {
  loadCommentsThroughOldestUnread,
  loadOlderComments,
  type OlderCommentsLoaded,
} from "work/proposals/comments/_store/load-comments-through-oldest-unread";
import {
  getCommentsByProposal,
  getCommentsLoadError,
  getForumCommentsLoad,
  type ForumCommentsLoad,
  getIsLoadingComments,
  populateThreadComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentAudienceSwitch from "work/proposals/comments/_view/comment-audience-switch";
import CommentComposer from "work/proposals/comments/_view/comment-composer";
import CommentItem from "work/proposals/comments/_view/comment-item";
import CommentTimelineFrame, { filteredEmptyText } from "work/proposals/comments/_view/comment-timeline-frame";
import ProposalCommentList from "work/proposals/comments/_view/proposal-comment-list";
import useCommentTimeline from "work/proposals/comments/_view/use-comment-timeline";
import useReadCommentsWhenSeen from "work/proposals/comments/_view/use-read-comments-when-seen";
import Comment from "work/proposals/comments/comment";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { conversationUnreadCount } from "work/proposals/comments/threads/conversation-unread-count";
import { offersNegotiation } from "work/proposals/comments/threads/offers-negotiation";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import Proposal from "work/proposals/proposal";
import type { ForumRequest } from "messaging/conversations/conversation-links";

// The negotiation this revision continues, above the one it is: its comments came first (#5741).
// Separated by a rule and dimmed a little rather than folded into the list below: they are two
// negotiations, and a comment written about the terms being revised is not a comment on the revision.
const CarriedOverSection = styled("div")(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  opacity: 0.85,
  paddingBottom: theme.spacing(1),
}));
const CarriedOverHeading = styled(Typography)(({ theme }) => ({
  paddingBottom: theme.spacing(0.5),
}));
const CarriedOverNote = styled(Typography)(({ theme }) => ({
  paddingBottom: theme.spacing(0.5),
}));
/**
 * A container, as the work agreement's message pane is, so the bubbles in it size themselves and their
 * info rows by the pane's width rather than the window's (#6607). Without one their container queries
 * matched nothing here: the name gave way in a 290px drawer however wide it could have been.
 */
const CommentStream = styled(CommentTimelineFrame)(({ theme }) => ({
  ...correspondenceListPadding(theme),
  containerType: "inline-size",
  paddingRight: theme.spacing(1),
  scrollbarGutter: "stable",
}));

/**
 * What the open conversation of the negotiation a revision continues has to show (#6631):
 * - `comments`: what was said in it;
 * - `nothing-said`: it was never opened, or the reader reads it, its comments loaded, and none is
 *   theirs to see;
 * - `unavailable`: it exists, or may, and the reader is refused it;
 * - `unknown`: an answer, or its comments, are still on their way, or failed for another reason.
 */
export type CarriedOverConversation = "comments" | "nothing-said" | "unavailable" | "unknown";

/** The empty text of a revision's own list when nothing was said before it either. */
export const nothingSaidEitherText = "No comments in this revision or before it.";
/** Under "Before this revision", when nothing was said there. */
export const nothingSaidBeforeText = "Nothing was said here before this revision.";
/** Under "Before this revision", when the reader may not read what was said there. */
export const earlierUnavailableText = "Earlier conversation isn't available to you.";

/**
 * Reads the server's answer about the reader's conversations in the negotiation a revision continues.
 * A comment the store already holds is shown whatever the answer says. Without one, the answer decides
 * whether nothing was said or the reader may not see it: the thread list leaves out a refused
 * conversation, so it cannot tell the two apart. A reader who takes part in that negotiation for
 * neither party is refused both conversations.
 *
 * A readable conversation is empty only once its own comments loaded. Both conversations share one list
 * in the store, so the list being there says nothing about this one: the other conversation's comments,
 * or one arriving live, can fill it first. Until this conversation loads, or when its load fails, the
 * answer stays `unknown`.
 */
export function carriedOverConversation(params: {
  conversations: readonly ProposalCommentConversationJSON[] | undefined;
  refused: boolean;
  comments: readonly Comment[] | undefined;
  isExternal: boolean;
  /** Whether the open conversation's own comments loaded, or failed to. */
  conversationLoad: ForumCommentsLoad | undefined;
}): CarriedOverConversation {
  const { conversations, refused, comments, isExternal, conversationLoad } = params;
  if (comments?.some((comment) => comment.thread.isExternal === isExternal)) return "comments";
  if (refused) return "unavailable";
  const availability = conversations?.find((conversation) => conversation.isExternal === isExternal)?.availability;
  if (availability === "unauthorized") return "unavailable";
  if (availability === "missing") return "nothing-said";
  if (availability === "available" && conversationLoad === "loaded") return "nothing-said";
  return "unknown";
}

/** The open conversation's forum in the negotiation a revision continues, when the server named one. */
function carriedOverForumId(
  conversations: readonly ProposalCommentConversationJSON[] | undefined,
  isExternal: boolean,
): string | undefined {
  return conversations?.find((conversation) => conversation.isExternal === isExternal)?.forumId ?? undefined;
}

/** The tab of the conversation both parties read. */
export const ALL_STAKEHOLDERS_TAB = "all-stakeholders";
/** The tab of the conversation only the reader's own party reads. */
export const OWN_TEAM_TAB = "own-team";

/**
 * What the conversation both parties read is called: the negotiation itself, the same on either side.
 *
 * It was named after the other side's lead (#5822), "Team Leader" on the client's side and "Client Rep"
 * on the vendor's. Once a proposal is submitted each party has a team leader of its own, so "Team
 * Leader" no longer said whose, and the two tabs named people rather than the kind of conversation each
 * one is. The server's own name for the forum, "All Stakeholders", is unchanged.
 */
export const negotiationTitle = "Negotiation";

/**
 * What the reader's own party's conversation is called: the one its team leader and reviewers hold
 * among themselves, on either side, which the other party never reads.
 */
export const ownTeamTitle = "Internal";

/** Who reads what the reader sends in the negotiation: both parties (#6875). */
const negotiationAudienceNotice = "Everyone on both teams can see what you send here.";

/** Who reads what the reader sends in Internal: their own party alone (#6875). */
const ownTeamAudienceNotice = "Only your team can see what you send here.";

type ProposalCorrespondenceProps = {
  /** A comment-scoped proposal: its id is the negotiation id the comment forums are filed under. */
  proposal: Proposal;
  commentThreads: CommentThread[];
  /** Whether the pane is on screen. The carried-over conversation loads only then. */
  isOpen: boolean;
  isSaving?: boolean;
  readOnly?: boolean;
  showMarkAllRead?: boolean;
  /** The negotiation this one revises, when it revises one and the reader may read it. */
  carriedOverNegotiationId?: string;
  /**
   * The proposal revises a negotiation the reader is refused (#6631). Without its envelope there is no
   * negotiation id to ask about, and the pane says the earlier conversation is not available on both tabs.
   */
  carriedOverRefused?: boolean;
  /** The comment to bring on screen: the oldest unread one, when "Prev / Next" arrives at it. */
  commentToScrollTo?: string;
  /**
   * The conversation a "Prev / Next" stop arrived at (#5824). The pane opens its tab and loads back to
   * its oldest unread comment. A new object for every arrival, so arriving at the same conversation
   * again opens it again.
   */
  conversationToOpen?: { readonly isExternal: boolean };
  /**
   * The conversation a link asked the pane to open on (#5826): a forum id, or a tab's name. One the
   * reader is not served leaves the pane on its default tab.
   */
  forumRequest?: ForumRequest;
};

/**
 * What a link may call the conversation both parties read: its tab's name, the server's name for its
 * forum, or either name the tab had before (#5822, #6515). A link is written once and opened later, so
 * one that still says "Team Leader" or "Client Rep" opens the conversation it meant, not Internal, where
 * a reply would never reach the other party.
 */
const counterpartyLinkNames = [negotiationTitle, "All Stakeholders", "Team Leader", "Client Rep"].map((name) =>
  name.toLocaleLowerCase(),
);

/** What a link may call the reader's own party's conversation: its tab's name, or "Reviewers", its name before #6515. */
const ownTeamLinkNames = [ownTeamTitle, "Reviewers"].map((name) => name.toLocaleLowerCase());

/** The thread `forum` names, by forum id or by its tab's name, ignoring case. */
export function findRequestedThread(threads: readonly CommentThread[], forum: string | undefined): CommentThread | undefined {
  const wanted = forum?.trim().toLocaleLowerCase();
  if (!wanted) return undefined;
  const isNamed = (thread: CommentThread) =>
    thread.isExternal ? counterpartyLinkNames.includes(wanted) : ownTeamLinkNames.includes(wanted);
  return (
    threads.find((thread) => thread.id?.value.toLocaleLowerCase() === wanted) ??
    threads.find((thread) => thread.id && isNamed(thread))
  );
}

/**
 * Marks every unread comment in one conversation read on the server, loaded or not. Outside the
 * component because it needs a `catch`, and the React Compiler skips a component whose code it cannot
 * lower.
 */
async function markConversationRead(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  forumId: string,
  readerId: Guid,
): Promise<void> {
  try {
    await dispatch(messagingApi.endpoints.markForumRead.initiate({ forumId, readerId })).unwrap();
  } catch (error) {
    console.error("Error marking all comments as read:", error);
    showErrorSnackbar(error, { doing: "marking the comments read", operation: "proposal.comments.mark-read" });
  }
}

/** Loads older comments until the oldest unread one is in the store, and answers with its id. */
async function loadOldestUnreadCommentId(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  session: Session,
  thread: CommentThread,
  readerId: Guid,
): Promise<string | undefined> {
  try {
    return await dispatch(loadCommentsThroughOldestUnread({ session, thread, readerId })).unwrap();
  } catch (error) {
    console.error("Failed to load the oldest unread comment", error);
    showErrorSnackbar(error, { doing: "loading the oldest unread comment", operation: "proposal.comments.load-oldest-unread" });
    return undefined;
  }
}

/** How near the top of the list, in pixels, the reader has to scroll for the older comments to load. */
const loadOlderThreshold = 48;

/**
 * The id of the comment at the foot of the list: the one that entered the conversation last, or one that
 * has not entered it yet, as `sortCommentsOldestFirst` orders them.
 */
function newestCommentIdOf(comments: readonly Comment[]): string | undefined {
  const enteredAt = (comment: Comment) => comment.enteredConversationAt?.valueOf() ?? Number.POSITIVE_INFINITY;
  let newest: Comment | undefined;
  for (const comment of comments) {
    if (!newest || enteredAt(comment) >= enteredAt(newest)) newest = comment;
  }
  return newest?.id?.value;
}

/** Loads the page of comments older than the loaded ones. Outside the component for its `catch`, as above. */
async function loadOlderCommentPage(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  session: Session,
  thread: CommentThread,
): Promise<OlderCommentsLoaded | undefined> {
  try {
    return await dispatch(loadOlderComments({ session, thread })).unwrap();
  } catch (error) {
    console.error("Failed to load older comments", error);
    showErrorSnackbar(error, { doing: "loading older comments", operation: "proposal.comments.load-older" });
    return undefined;
  }
}

/**
 * A proposal's Correspondence pane, built as the work agreement's is: a tab per conversation; a search
 * header over the list and a date scrubber beside it; the composer under it. Neither pane draws a member
 * list: the proposal's teams decide who reads each conversation (#5823). Unlike the work agreement's, it
 * has no Shared files tab, because a proposal's comments carry text only (#5819).
 *
 * The conversations are the negotiation's two audiences: "Negotiation", which both parties read, and
 * "Internal", which only the reader's own party reads. A proposal nobody has submitted yet has no other
 * party to talk to, so until then the pane offers "Internal" alone (see `offersNegotiation`).
 * These are the same two forums the "External Comment" switch chose between. The tab replaced the
 * switch, so a reader always sees which conversation a comment joins, and a comment is written into
 * the conversation on screen. The server decides who reads each forum, as it always did; the tab only
 * chooses which of the reader's own forums to show.
 *
 * Everything the pane did before stays: read state, Mark all read, the oldest-unread jump, and the
 * author's edit, retract, delete and re-aim.
 *
 * Read state is the open tab's own (#5824). A comment is marked read when the reader scrolls it into
 * view (`useReadCommentsWhenSeen`), and Mark all read marks the open conversation. Opening the pane marks
 * nothing, and nothing on one tab marks the other tab's comments read.
 */
export default function ProposalCorrespondence(props: Readonly<ProposalCorrespondenceProps>) {
  const {
    proposal,
    commentThreads,
    isOpen,
    carriedOverNegotiationId,
    carriedOverRefused,
    commentToScrollTo,
    conversationToOpen,
    forumRequest,
  } = props;
  const isSaving = props.isSaving ?? false;
  const readOnly = props.readOnly ?? false;
  const showMarkAllRead = props.showMarkAllRead ?? true;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const bottomRef = useRef<HTMLDivElement>(null);

  const negotiationIsOffered = offersNegotiation(proposal);

  // A new comment is written to the reader's own team unless they choose the negotiation, as the
  // switch this replaces defaulted to internal. The choice is held as made, and read through
  // `negotiationIsOffered`, so a link or a "Prev / Next" stop cannot open a tab the pane does not offer.
  const [chosenTab, setOpenTab] = useState<string>(OWN_TEAM_TAB);
  const [chosenIsExternal, setConversationIsExternal] = useState(false);
  const openTab = negotiationIsOffered ? chosenTab : OWN_TEAM_TAB;
  const conversationIsExternal = negotiationIsOffered && chosenIsExternal;
  const [searchString, setSearchString] = useState("");
  // Remounts the search box when the search is cleared from outside it, so the box does not keep text
  // that no longer filters anything.
  const [searchGeneration, setSearchGeneration] = useState(0);
  const [jumpToBottomVisible, setJumpToBottomVisible] = useState(false);
  // The comment Jump to oldest unread brought on screen. The scroll to the foot stays off while it is
  // there, or the older pages it loaded would carry the list straight back down.
  const [unreadJumpTargetId, setUnreadJumpTargetId] = useState<string>();
  const [isJumpingToUnread, setIsJumpingToUnread] = useState(false);
  // The pane opens on a conversation's newest page and loads the page before it as the reader scrolls
  // to the top (#6769). Keyed by forum id: each conversation has its own history. Refs, not state: a
  // scroll fires many events before the pane renders again, and each must see the load already asked for.
  const loadingOlderForumId = useRef<string>(undefined);
  const fullyLoadedForumIds = useRef(new Set<string>());
  // The reader's distance from the foot of the list when older comments were asked for. Older rows go
  // in above the reader, so keeping this distance keeps the rows they were reading where they were.
  const distanceFromFootBeforeOlder = useRef<number>(undefined);
  const unreadCounts = useSelector(getForumUnreadCounts);
  const readWhenSeen = useReadCommentsWhenSeen({ proposalId: proposal.id ?? undefined, isOpen });

  const proposalComments = useSelector(getCommentsByProposal(proposal.id));
  const isLoadingComments = useSelector(getIsLoadingComments(proposal.id));
  const commentsLoadError = useSelector(getCommentsLoadError(proposal.id));
  const carriedOverConversationId = useMemo(
    () => (carriedOverNegotiationId ? Guid.fromJSON(carriedOverNegotiationId) ?? undefined : undefined),
    [carriedOverNegotiationId],
  );
  const carriedOverComments = useSelector(getCommentsByProposal(carriedOverConversationId));
  const priorConversations = useGetProposalCommentConversationsQuery(
    { negotiationId: carriedOverNegotiationId ?? "" },
    { skip: !carriedOverNegotiationId || !isOpen },
  );

  const inConversation = (comment: Comment) => comment.thread.isExternal === conversationIsExternal;
  const normalizedSearch = searchString.trim().toLocaleLowerCase();
  const matchesSearch = (comment: Comment) =>
    !normalizedSearch || comment.content.toLocaleLowerCase().includes(normalizedSearch);

  // One scrubber over both negotiations the open conversation lists: the carried-over one is this
  // negotiation's past, and a reader looking for what was said in March should not have to know which
  // side of the revision it fell on.
  const scrollTargetId = unreadJumpTargetId ?? commentToScrollTo;
  const timeline = useCommentTimeline({
    comments: [...(carriedOverComments ?? []), ...(proposalComments ?? [])].filter(inConversation),
    revealCommentId: scrollTargetId,
  });
  const listed = (comment: Comment) => inConversation(comment) && matchesSearch(comment) && timeline.includes(comment);

  // "Prev / Next" arrives at the oldest unread comment, which may be in the other conversation. The
  // pane opens that one, and drops a search that would hide the comment. Adjusted while rendering, as
  // `useCommentTimeline` adjusts its filter, once the comment is in the store.
  const revealTarget = commentToScrollTo
    ? proposalComments?.find((comment) => comment.id?.value === commentToScrollTo)
    : undefined;
  const [revealedCommentId, setRevealedCommentId] = useState<string>();
  if (revealTarget && commentToScrollTo !== revealedCommentId) {
    setRevealedCommentId(commentToScrollTo);
    setConversationIsExternal(revealTarget.thread.isExternal);
    setOpenTab(revealTarget.thread.isExternal ? ALL_STAKEHOLDERS_TAB : OWN_TEAM_TAB);
    if (!matchesSearch(revealTarget)) {
      setSearchString("");
      setSearchGeneration(searchGeneration + 1);
    }
  }

  // A "Prev / Next" stop on a conversation opens that conversation's tab (#5824), whether or not its
  // oldest unread comment is loaded yet, and then loads back to it: the stop is for every unread comment
  // the tab counts, and the pane loads only the newest page. Adjusted while rendering, as above. A
  // search is dropped on every arrival, not only when the target is already loaded: a target the load
  // brings in later would otherwise be hidden by it, with nothing to scroll to.
  const [openedConversation, setOpenedConversation] = useState<{ readonly isExternal: boolean }>();
  const [arrivalToLoad, setArrivalToLoad] = useState<{ thread: CommentThread }>();
  if (conversationToOpen && conversationToOpen !== openedConversation) {
    setOpenedConversation(conversationToOpen);
    setConversationIsExternal(conversationToOpen.isExternal);
    setOpenTab(conversationToOpen.isExternal ? ALL_STAKEHOLDERS_TAB : OWN_TEAM_TAB);
    setUnreadJumpTargetId(undefined);
    if (searchString) {
      setSearchString("");
      setSearchGeneration(searchGeneration + 1);
    }
    const thread = commentThreads.find((candidate) => candidate.id && candidate.isExternal === conversationToOpen.isExternal);
    setArrivalToLoad(thread ? { thread } : undefined);
  }
  // The load starts only once the conversation's first page of comments is in the store (#6585). It
  // fetches the pages after the first, so a link that arrives with the threads, before their comments
  // load, would find a short later page, stop, and answer with nothing to scroll to. Latched, so the
  // pages the load itself fetches, which set the loading flag again, do not cancel it.
  const [firstPageArrival, setFirstPageArrival] = useState<{ thread: CommentThread }>();
  if (arrivalToLoad && arrivalToLoad !== firstPageArrival && proposalComments !== undefined && !isLoadingComments) {
    setFirstPageArrival(arrivalToLoad);
  }
  // The load answers this arrival only while the reader is still on it: the page still asks for it, the
  // pane is open, and its tab is the open one. Leaving any of those cancels it, so the cross-party
  // conversation's answer can never become the scroll target of the Internal tab.
  const arrivalIsCurrent = Boolean(
    arrivalToLoad &&
      arrivalToLoad === firstPageArrival &&
      conversationToOpen === openedConversation &&
      isOpen &&
      arrivalToLoad.thread.isExternal === conversationIsExternal,
  );
  useEffect(() => {
    const readerId = session.user?.id;
    if (!arrivalToLoad || !arrivalIsCurrent || !readerId) return;
    let superseded = false;
    void loadOldestUnreadCommentId(dispatch, session, arrivalToLoad.thread, readerId).then((commentId) => {
      if (!superseded && commentId) setUnreadJumpTargetId(commentId);
    });
    return () => {
      superseded = true;
    };
  }, [arrivalIsCurrent, arrivalToLoad, dispatch, session]);

  // A link opens the pane on the conversation it names, once that conversation's thread is in the
  // store. Each new request selects it again; between requests the reader's own choice stands. It is
  // the reader choosing that tab, so it resets what `openConversationTab` resets: a pane kept mounted
  // may be holding a search typed in the view it is leaving, or still loading back to a "Prev / Next"
  // arrival's oldest unread comment. The link is an arrival of its own (#6585): the pane loads back to
  // the conversation's oldest unread comment, as a "Prev / Next" stop does, and stays at the foot when
  // nothing is unread.
  const requestedThread = findRequestedThread(commentThreads, forumRequest?.forum);
  const [honoredForumRequestKey, setHonoredForumRequestKey] = useState<string>();
  if (forumRequest && requestedThread && forumRequest.key !== honoredForumRequestKey) {
    setHonoredForumRequestKey(forumRequest.key);
    setJumpToBottomVisible(false);
    setUnreadJumpTargetId(undefined);
    setArrivalToLoad({ thread: requestedThread });
    if (searchString) {
      setSearchString("");
      setSearchGeneration(searchGeneration + 1);
    }
    setConversationIsExternal(requestedThread.isExternal);
    setOpenTab(requestedThread.isExternal ? ALL_STAKEHOLDERS_TAB : OWN_TEAM_TAB);
  }

  /**
   * A conversation's tab counts what the server counts: every unread comment in it, loaded or not, kept
   * current by realtime and by every read (#5828). The loaded ones until the server has sent a count.
   */
  const unreadCount = (isExternal: boolean) =>
    conversationUnreadCount({
      commentThreads,
      isExternal,
      serverCounts: unreadCounts,
      comments: proposalComments,
      readerId: session.user?.id,
    });

  const conversationThread = commentThreads.find(
    (thread) => thread.id && thread.isExternal === conversationIsExternal,
  );

  // Comment bodies are normally loaded when the negotiation's threads enter the store. This is the
  // fallback for threads that are in the store without their comments. It loads once and settles: a
  // loaded (even empty) answer and a failed one both stop it.
  useEffect(() => {
    if (isLoadingComments || proposalComments !== undefined || commentsLoadError) return;
    commentThreads.forEach((thread) => {
      void dispatch(populateThreadComments({ session, thread }));
    });
  }, [commentThreads, commentsLoadError, dispatch, isLoadingComments, proposalComments, session]);

  /**
   * Scrolls the list, and only the list, to its foot. Not `scrollIntoView`: that scrolls every
   * scrollable ancestor too, and the proposal page clips the pane in a column of its own. Scrolling that
   * column sideways slid the pane under the page, cutting off its left edge.
   */
  function scrollListToFoot() {
    const list = bottomRef.current?.parentElement;
    list?.scrollTo?.({ top: list.scrollHeight, behavior: "smooth" });
  }

  // Which comments the open conversation holds, and nothing else about them. A read receipt replaces
  // every comment it answers for, and the reader is often scrolled up to them when it lands: keyed on
  // the comments themselves, a scroll pulled the reader back to the foot on every receipt.
  const conversationComments = (proposalComments ?? []).filter(inConversation);
  const conversationCommentIds = conversationComments.map((comment) => comment.id?.value ?? "").join("|");
  // Only a newer comment moves the foot. Older ones load above the reader as they scroll up (#6769),
  // and following them to the foot would carry the reader away from what they scrolled up to read.
  const newestCommentId = newestCommentIdOf(conversationComments);

  // Scrolls to the newest comment, the foot of the list, on open, on a change of conversation and as new
  // ones arrive. `commentToScrollTo` overrides this: the unread stop opens the pane on a row of its own
  // choosing, which `CommentItem`'s own `scrollIntoView` reaches instead. Deferred so it runs after the
  // rows have mounted.
  useEffect(() => {
    if (!isOpen || scrollTargetId) return;
    const timeoutId = globalThis.setTimeout(() => {
      scrollListToFoot();
    }, 0);
    return () => globalThis.clearTimeout(timeoutId);
  }, [newestCommentId, conversationIsExternal, isOpen, scrollTargetId]);

  // Older comments went in above the reader: put the rows they were reading back where they were, before
  // the browser paints the list moved.
  useLayoutEffect(() => {
    const distanceFromFoot = distanceFromFootBeforeOlder.current;
    const list = bottomRef.current?.parentElement;
    if (distanceFromFoot === undefined || !list) return;
    distanceFromFootBeforeOlder.current = undefined;
    // `scrollTo` is optional-called because jsdom has none.
    list.scrollTo?.({ top: list.scrollHeight - distanceFromFoot });
  }, [conversationCommentIds]);

  /**
   * Loads the open conversation's page of comments before the oldest loaded one, unless that page is
   * on its way already or the conversation's oldest comment is loaded.
   */
  function loadOlder(list: HTMLElement) {
    const forumId = conversationThread?.id?.value;
    if (!conversationThread || !forumId || loadingOlderForumId.current || fullyLoadedForumIds.current.has(forumId)) return;
    loadingOlderForumId.current = forumId;
    distanceFromFootBeforeOlder.current = list.scrollHeight - list.scrollTop;
    void loadOlderCommentPage(dispatch, session, conversationThread).then((loaded) => {
      loadingOlderForumId.current = undefined;
      // Nothing went in above the reader, so there is no place to keep.
      if (!loaded?.added) distanceFromFootBeforeOlder.current = undefined;
      if (loaded?.reachedOldest) fullyLoadedForumIds.current.add(forumId);
    });
  }

  const nothingUnreadHere =
    unreadCount(conversationIsExternal) === 0 &&
    !(proposalComments ?? []).some((comment) => inConversation(comment) && comment.isUnreadBy(session.user?.id));

  /**
   * Marks every comment the reader has not read in the open conversation read: loaded or not. Only the
   * open one (#5824): the other tab's comments are still waiting to be read.
   */
  function markAllRead() {
    const readerId = session.user?.id;
    const forumId = conversationThread?.id?.value;
    if (!readerId || !forumId || nothingUnreadHere) return;
    void markConversationRead(dispatch, forumId, readerId);
  }

  /**
   * Brings the open conversation's oldest unread comment on screen, however far back it is: older pages
   * load until it is in the list.
   */
  function jumpToOldestUnread() {
    const readerId = session.user?.id;
    if (!readerId || !conversationThread) return;
    setIsJumpingToUnread(true);
    void loadOldestUnreadCommentId(dispatch, session, conversationThread, readerId).then((commentId) => {
      setIsJumpingToUnread(false);
      if (commentId) setUnreadJumpTargetId(commentId);
    });
  }

  function openConversationTab(tab: string) {
    setJumpToBottomVisible(false);
    setUnreadJumpTargetId(undefined);
    // Choosing a tab by hand ends the arrival: coming back to its tab does not load or scroll for it again.
    setArrivalToLoad(undefined);
    // Each view has its own search: the one typed in the view being left does not follow the reader.
    setSearchString("");
    setSearchGeneration(searchGeneration + 1);
    setOpenTab(tab);
    if (tab === ALL_STAKEHOLDERS_TAB) setConversationIsExternal(true);
    if (tab === OWN_TEAM_TAB) setConversationIsExternal(false);
  }

  function handleScrolled(element: HTMLElement) {
    const maxScroll = element.scrollHeight - element.clientHeight;
    setJumpToBottomVisible(maxScroll > 0 && element.scrollTop / maxScroll < 0.99);
    if (maxScroll > 0 && element.scrollTop <= loadOlderThreshold) loadOlder(element);
  }

  const carriedOverConversationLoad = useSelector(
    getForumCommentsLoad(carriedOverForumId(priorConversations.data, conversationIsExternal)),
  );
  let carriedOver: CarriedOverConversation | undefined;
  if (carriedOverNegotiationId) {
    carriedOver = carriedOverConversation({
      conversations: priorConversations.data,
      refused:
        isFetchBaseQueryError(priorConversations.error) &&
        priorConversations.error.status === HttpStatusCode.Forbidden,
      comments: carriedOverComments,
      isExternal: conversationIsExternal,
      conversationLoad: carriedOverConversationLoad,
    });
  } else if (carriedOverRefused) {
    carriedOver = "unavailable";
  }
  const filtering = Boolean(normalizedSearch) || timeline.filtering;

  function emptyText(): string {
    if (normalizedSearch) return "No Matching Results";
    if (timeline.filtering) return filteredEmptyText;
    if (carriedOver === "nothing-said") return nothingSaidEitherText;
    return readOnly ? "No comments to display" : "No comments yet";
  }

  return (
    <CorrespondenceFrame fillAvailableSpace data-testid="proposal-correspondence">
      <CorrespondenceTabs
        value={openTab}
        onChange={openConversationTab}
        conversations={[
          ...(negotiationIsOffered
            ? [{ value: ALL_STAKEHOLDERS_TAB, title: negotiationTitle, unreadCount: unreadCount(true) }]
            : []),
          {
            value: OWN_TEAM_TAB,
            title: ownTeamTitle,
            unreadCount: unreadCount(false),
          },
        ]}
        showSharedFiles={false}
      />
      <CorrespondenceBody>
        <CorrespondenceHeader audienceNotice={conversationIsExternal ? negotiationAudienceNotice : ownTeamAudienceNotice}>
          <CorrespondenceSearchBar
            key={`${openTab}-${searchGeneration}`}
            filterDates={timeline.filtering}
            datesDisabled={!timeline.firstDate}
            onToggleDates={timeline.toggle}
            onSearch={setSearchString}
            onMarkAllRead={showMarkAllRead ? markAllRead : undefined}
            markAllReadDisabled={nothingUnreadHere}
            onJumpToOldestUnread={!normalizedSearch && !timeline.filtering ? jumpToOldestUnread : undefined}
            jumpToOldestUnreadDisabled={unreadCount(conversationIsExternal) === 0 || isJumpingToUnread}
          />
        </CorrespondenceHeader>
        <CommentStream
          timeline={timeline}
          data-testid="proposal-comments"
          onScroll={(event) => handleScrolled(event.currentTarget)}
        >
          {/* The negotiation this one revises, above the one it is: comments carried over are the
              oldest in the conversation, so they lead it rather than trailing it (#5741). */}
          {carriedOverNegotiationId && (
            <CarriedOverComments
              negotiationId={carriedOverNegotiationId}
              enabled={isOpen}
              include={listed}
              conversation={carriedOver ?? "unknown"}
              filtering={filtering}
            />
          )}
          {!carriedOverNegotiationId && carriedOverRefused && !filtering && (
            <CarriedOverNotice conversation="unavailable" />
          )}
          {/* A comment renders as the comment. This is the only surface a negotiation's comments
              are read on, so it is the only place their author can edit, retract, delete or re-aim
              them and a recipient can mark one unread. */}
          <ProposalCommentList
            conversationId={proposal.id}
            emptyText={emptyText()}
            include={listed}
            renderComment={(comment) => (
              <CommentItem
                comment={comment}
                readOnly={readOnly}
                scrollIntoView={Boolean(scrollTargetId) && comment.id?.value === scrollTargetId}
                readWhenSeen={readWhenSeen}
                // Re-aiming a comment at the negotiation is as unavailable as its tab.
                renderAudienceSwitch={
                  readOnly || !negotiationIsOffered
                    ? undefined
                    : (audience) => <CommentAudienceSwitch proposal={proposal} {...audience} />
                }
              />
            )}
          />
          <div ref={bottomRef} />
        </CommentStream>
        {(proposalComments?.length ?? 0) > 0 && (
          <CorrespondenceJumpToBottom
            visible={jumpToBottomVisible}
            onClick={() => {
              setUnreadJumpTargetId(undefined);
              scrollListToFoot();
            }}
          />
        )}
      </CorrespondenceBody>
      <CorrespondenceComposerSlot>
        <CommentComposer
          proposal={proposal}
          isExternal={conversationIsExternal}
          isSaving={isSaving}
          readOnly={readOnly}
          onCommentPosted={() => {
            globalThis.setTimeout(scrollListToFoot, 0);
          }}
        />
      </CorrespondenceComposerSlot>
    </CorrespondenceFrame>
  );
}

/** "Before this revision", saying that nothing was said there, or that the reader may not read it. */
function CarriedOverNotice(props: Readonly<{ conversation: "nothing-said" | "unavailable" }>) {
  return (
    <CarriedOverSection data-testid="carried-over-negotiation">
      <CarriedOverHeading variant="subtitle2" color="text.secondary">
        Before this revision
      </CarriedOverHeading>
      <CarriedOverNote variant="body2" color="text.secondary" data-testid="carried-over-note">
        {props.conversation === "unavailable" ? earlierUnavailableText : nothingSaidBeforeText}
      </CarriedOverNote>
    </CarriedOverSection>
  );
}

/**
 * The conversation of the negotiation a revision continues, read-only (#5221).
 *
 * It loads itself, the first time the pane is open. Under its heading it lists what was said, or says
 * that nothing was, or that the reader may not read it (#6631): a section that vanished left the reader
 * unable to tell a refusal from a silence. A search or date filter that leaves nothing of it leaves no
 * section, heading and all, as it leaves nothing of any other list.
 */
function CarriedOverComments(
  props: Readonly<{
    negotiationId: string;
    enabled: boolean;
    include: (comment: Comment) => boolean;
    conversation: CarriedOverConversation;
    /** Whether a search or date filter narrows the list. */
    filtering: boolean;
  }>,
) {
  const { negotiationId, enabled, include, conversation, filtering } = props;
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const conversationId = useMemo(() => Guid.fromJSON(negotiationId) ?? undefined, [negotiationId]);
  const threads = useSelector(getCommentThreadsByProposal(undefined, conversationId));
  const isLoadingThreads = useSelector(getIsLoadingCommentThreadsByProposal(undefined, conversationId));
  const threadsLoadError = useSelector(getErrorLoadingCommentThreadsByProposal(undefined, conversationId));
  const comments = useSelector(getCommentsByProposal(conversationId));

  useEffect(() => {
    if (!enabled || !conversationId || threads || isLoadingThreads || threadsLoadError) return;
    void dispatch(populateCommentThreadsByProposal({ session, proposalId: conversationId }));
  }, [conversationId, dispatch, enabled, isLoadingThreads, session, threads, threadsLoadError]);

  if (conversation === "unknown" || (conversation !== "comments" && filtering)) return null;
  if (conversation === "comments" && !comments?.some(include)) return null;

  if (conversation !== "comments") return <CarriedOverNotice conversation={conversation} />;

  return (
    <CarriedOverSection data-testid="carried-over-negotiation">
      <CarriedOverHeading variant="subtitle2" color="text.secondary">
        Before this revision
      </CarriedOverHeading>
      {/* Read-only throughout: those comments belong to a negotiation that is over, and their authors
          cannot edit, retract or re-aim them from here. */}
      <ProposalCommentList
        conversationId={conversationId}
        emptyText="No comments to display"
        include={include}
        renderComment={(comment) => <CommentItem comment={comment} readOnly />}
      />
    </CarriedOverSection>
  );
}
